import { ChangeEvent, useEffect, useState} from "react";
import { LABELS } from "~/resources/Labels";
import { FormScriptResponse, PropRow, SortTable } from "~/models/Commons ";
import {
  useCopyScriptMutation,
  useDeleteScriptMutation,
  useGetScriptsQuery,
} from "~/services/script";
import {
  attachResourcesToScripts,
  base64ToArrayBuffer,
  displayEntries,
  downloadBlob,
  
  sortScripts,
} from "../../Utils/Utils";
import { ScriptsHeader } from "~/models/scripts/ScriptsHeader";
import { SIMPLTable } from "../../components/commons/table/SimplTable";
import { Layout } from "../../components/commons/layout/Layout";
import AddIcon from "@mui/icons-material/Add";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { SIMPLAlert } from "../../components/commons/dialog/SimplAlert";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { PayloadType } from "~/models/types/Payload";
import { Entries } from "~/models/enums/Entries";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { handleCopyEntity } from "~/Utils/helpers/CopyEntityHelper";
import { Alerts } from "../messages/Alerts";
import { handleDeleteEntity } from "~/Utils/helpers/DeleteEntityHelper";
import AddScript from "./AddScript";
import ConfigFile from "./ConfigFile";
import DetailViewScript from "./DetailViewScript";
import { useTranslation } from "react-i18next";


export const Scripts = () => {
  const { t } = useTranslation();
  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [rows, setRows] = useState([] as PropRow[]);
  const [openDetailView, setOpenDetailView] = useState(false);
  const [openConfigFile, setOpenConfigFile] = useState(false);
  const [item, setItem] = useState({} as FormScriptResponse);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [deleteScript] = useDeleteScriptMutation();
  const [size, setSize] = useState<number>(5);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState(true);
  const [copyModal, setCopyModal] = useState(false);
  const [copyModalSuccess, setCopyModalSuccess] = useState(false);
  const [copyModalError, setCopyModalError] = useState(false);

  type ResponseDetails = {
    title: string;
    message: string;
  };
  const [responseDetails, setResponseDetails] =
    useState<ResponseDetails | null>(null);
  const [copyScript] = useCopyScriptMutation();
  const { data, refetch, isLoading, isFetching, isError } = useGetScriptsQuery({
    page: 0,
    size: 500,
    active: isActive,
  });
  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });

  const initScripts = () => {
    const payload = data?.payload as PayloadType | undefined;

    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }

    const scriptMergedList = attachResourcesToScripts(payload);
    const sortedScripts = sortScripts(scriptMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, totalElements, 999);

    const totalPages = payload?.page?.totalPages ?? 0;
    setTotal(Number(totalPages));

    if (payload?.page?.totalElements) {
      setTotalElements(payload.page.totalElements);
    }

    const scriptRows: PropRow[] = customizedList.map((scriptItem: any) => ({
      row: scriptItem,
    }));

    setRows(scriptRows);
    setShowPagination(true);
  };

  useEffect(() => {
    initScripts();
  }, [data, true, sortConfig, size, total, totalElements]);

  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [true, size]);

  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) {
      setPage(0);
    }
  };

  const showFilterHandler = () => setShowFilter((prev) => !prev);
  const handleCloseAddModal = () => setIsAddModalOpen(false);
  const handleCloseConfigFileModal = () => setOpenConfigFile(false);

  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(e.target.value));
  };

  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  const renderButtonAddScript = () => (
    <SIMPLButtonCustomized
      label={t('AddScript')}
      buttonHandlerAction={() => setIsAddModalOpen(true)}
      secondIcon={<AddIcon fontSize="small" />}
      primary
    />
  );

  const renderAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t('Cancel')}
          secondary
          buttonHandlerAction={() => setDeleteModal(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t('Delete')} 
          danger
          buttonHandlerAction={handleDeleteItem}
        />
      </div>
    </>
  );

  const renderDeleteAlert = () =>
    deleteModal && (
      <SIMPLAlert
        onCancel={() => setDeleteModal(false)}
        message={t('MessageDialogDelete', { item: item.title })}
        title={t('DeleteScript')}
        actions={renderAlertButtons()}
      />
    );

  const handleConfirmDelete = (scriptItem: FormScriptResponse) => {
    if (scriptItem.scriptIdentify.deploymentScriptId) {
      setItem(scriptItem);
      setDeleteModal(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleDeleteItem = async () => {
    handleDeleteEntity({
      deleteFn: () =>deleteScript(item.scriptIdentify.deploymentScriptId).unwrap(),
      id: item.scriptIdentify.deploymentScriptId,
      refetchFn: refetch,
      onBeforeDelete: () => setDeleteModal(false),
      onSuccess: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setCopyModalError(false);
        setCopyModalSuccess(true);
      },
      onError: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setCopyModalError(true);
        setCopyModalSuccess(false);
      },
      t
    });
  };

  const handleRowClick = (scriptItem: FormScriptResponse) => {
    if (!scriptItem.active) {
      setItem(scriptItem);
      setOpenDetailView(true);
    }
  };

  const handleOpenDetail = (scriptItem: FormScriptResponse) => {
    if (scriptItem.active) {
      setItem(scriptItem);
      setOpenDetailView(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleConfigFile = (scriptItem: FormScriptResponse) => {
    if (scriptItem.active) {
      setItem(scriptItem);
      setOpenConfigFile(true);
    } else {
      console.warn(LABELS.ScriptIdDonotExist);
    }
  };

  const handleDownload = (scriptItem: FormScriptResponse) => {
    const arrayBuffer = base64ToArrayBuffer(scriptItem.file);
    downloadBlob(arrayBuffer, `${scriptItem.originalFileName}`);
  };
  /*Renders copy alert with cancel and delete actions */
  const renderCopyAlert = () =>
    copyModal && (
      <SIMPLAlert
        onCancel={() => setCopyModal(false)}
        message={t('MessageDialogCopy', { item: item.title })}
        title={t('CopyScripts')}
        actions={renderCopyAlertButtons()}
      />
    );
  /* Renders "Cancel" and "Copy Item" buttons for alerts */
  const renderCopyAlertButtons = () => (
    <>
      <div className="grow-0 basis-32 mr-5">
        <SIMPLButtonCustomized
          label={t("Cancel")}
          secondary
          buttonHandlerAction={() => setCopyModal(false)}
        />
      </div>
      <div className="grow-0 basis-32">
        <SIMPLButtonCustomized
          label={t("Copy")}
          primary
          firstIcon={<FileCopyIcon fontSize="small" />}
          buttonHandlerAction={handleCopyScript}
        />
      </div>
    </>
  );

  const handleConfirmCopy = (deploymentScriptId: FormScriptResponse) => {
    if (deploymentScriptId.scriptIdentify.deploymentScriptId) {
      setItem(deploymentScriptId);
      setCopyModal(true);
    } else {
      console.warn(LABELS.TemplateDoesNotExist);
    }
  };
  const handleCopyScript = () =>
    handleCopyEntity({
      copyFn: () => copyScript(item.scriptIdentify.deploymentScriptId).unwrap(),
      id: item.scriptIdentify.deploymentScriptId,
      refetchFn: refetch,
      onBeforeCopy: () => setCopyModal(false),
      onSuccess: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setCopyModalError(false);
        setCopyModalSuccess(true);
      },
      onError: (title, message) => {
        setResponseDetails({
          title,
          message,
        });
        setCopyModalSuccess(false);
        setIsAddModalOpen(false);
        setOpenConfigFile(false);
        setCopyModalError(true);
      },
      t
    });
  const renderAlertsHandler = () => (
    <Alerts
      showSuccess={copyModalSuccess}
      showError={copyModalError}
      title={responseDetails?.title}
      successMessage={responseDetails?.message || ""}
      errorMessage={responseDetails?.message || ""}
      onCloseSucessDialog={() => setCopyModalSuccess(false)}
      onCloseErrorDialog={() => setCopyModalError(false)}
      enabledSuccess={copyModalSuccess}
      enabledError={copyModalError}
    />
  );
  const renderSIMPLTable = () => (
   
          <SIMPLTable
            title={t('ScriptManagmentTitle')}
            custumizedNoDataAvailable={t('NoDataAvailable')}
            clear={handleResetAll}
            page={page}
            size={size}
            showPagination={showPagination}
            active={true}
            showHandler={showFilterHandler}
            filterActive={showFilter}
            tableHeaders={ScriptsHeader}
            rows={rows}
            onChangePage={handlePageChange}
            onChange={handleSizeChange}
            button={renderButtonAddScript()}
            sortConfig={sortConfig}
            handleSort={handleSort}
            defaultSortKey="dateAdded_raw"
            chipLabels={[]}
            handleOpenDetail={handleOpenDetail}
            handleConfigFile={handleConfigFile}
            handleRowClick={handleRowClick}
            handleDownload={handleDownload}
            handleDelete={handleConfirmDelete}
            handleOpenCopy={handleConfirmCopy}
            showButtonDeleteInModal
            showButtonDetailsInModal
            showButtonDownloadInModal={false}
            showButtonDecommissionInModal={false}
            showReviewOutputTemplateInModal={false}
            showEditInModal={false}
            showCopyInModal
            handlerChips={()=>{}}
            setIsActive={setIsActive}
            isActive={isActive}
            activeLabel={t('Active')}
            inactiveLabel={t('Inactive')}
            tabs
          />
  );


  return (
    <SIMPLMenuHeader>
      {renderAlertsHandler()}
      <Layout>
        {renderDeleteAlert()}
        {renderCopyAlert()}
        {/* Lazy-loaded Modals with Suspense */}
          {isAddModalOpen && (
            <AddScript
              onCloseModal={handleCloseAddModal}
              editedItem={null}
              setConfig={setSortConfig}
              title={t('AddScript')}
              refetch={refetch}
              onSuccess={(title, message) => {
                setResponseDetails({ title, message });
                setCopyModalSuccess(true);
                setCopyModalError(false);
              }}
              onError={(title, message) => {
                setResponseDetails({ title, message });
                setCopyModalError(true);
                setCopyModalSuccess(false);
              }}
            />
          )}
            {openConfigFile && (
            <ConfigFile
              item={item}
              onCloseModal={handleCloseConfigFileModal}
              editedItem={item}
              setConfig={setSortConfig}
              refetch={refetch}
              onSuccess={(title, message) => {
                setResponseDetails({ title, message });
                setCopyModalError(false);
                setCopyModalSuccess(true);
              }}
              onError={(title, message) => {
                setResponseDetails({ title, message });
                setCopyModalSuccess(false);
                setCopyModalError(true);
              }}
            />
          )}
          
         {openDetailView && (
            <DetailViewScript
              item={item}
              onClose={() => setOpenDetailView(false)}
            />
          )}

        <LoadingWrapper
          loadingTitle={t('LoadingScripts')}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          {renderSIMPLTable()}
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
